import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

export default function MentalHealthCarePage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    { question: "What is a care plan for mental health?" },
    { question: "Benefits of online mental health support?" },
    { question: "Is it worth doing a mental health first aid course?" },
    { question: "When is men's mental health month?" },
    { question: "Why organizations choose your EAP?" }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Hero Section */}
      <section className="bg-gray-200 py-12 md:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-black">
            Mental Health Care
          </h1>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-12 md:py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Left Content */}
            <div className="lg:w-1/2 space-y-8">
              {/* What is mental health care in the UK? */}
              <div>
                <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-black mb-4">
                  What is mental health care in the UK?
                </h2>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4">
                  Mental health care in the UK includes services like counselling, 
                  therapy, and medication management. The <span className="underline">Health City</span> offers free 
                  assessment, while private services provide personalized support.
                </p>
              </div>

              {/* Common mental health issues */}
              <div>
                <h3 className="text-base md:text-lg font-bold text-black mb-3">
                  Common mental health issues?
                </h3>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4">
                  Common mental health issues include anxiety, depression, stress, 
                  and bipolar disorder. These conditions affect mood, behavior, and 
                  thinking. At Health City, we assess and treat these issues with 
                  therapy, <span className="underline">counseling, and coping techniques</span> to improve overall 
                  mental well-being.
                </p>
              </div>

              {/* Mental health care assistant */}
              <div>
                <h3 className="text-base md:text-lg font-bold text-black mb-3">
                  Mental health care assistant at Health City?
                </h3>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                  Mental health care assistants at Health City support clients with 
                  daily tasks and emotional needs, working closely with therapists 
                  to implement treatment plans. They play a vital role in ensuring 
                  comprehensive care and aiding recovery.
                </p>
              </div>
            </div>
            
            {/* Right Content - Doctor Image */}
            <div className="lg:w-1/2">
              <div className="bg-gray-300 rounded-lg h-64 md:h-80 lg:h-96 flex items-center justify-center">
                {/* This would contain the doctor consultation image */}
                <span className="text-gray-600">Doctor Consultation Image</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Second Content Section */}
      <section className="py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Left Content - Therapy Image */}
            <div className="lg:w-1/2 order-2 lg:order-1">
              <div className="bg-gray-300 rounded-lg h-64 md:h-80 lg:h-96 flex items-center justify-center">
                {/* This would contain the therapy session image */}
                <span className="text-gray-600">Therapy Session Image</span>
              </div>
            </div>
            
            {/* Right Content */}
            <div className="lg:w-1/2 space-y-8 order-1 lg:order-2">
              {/* Symptoms */}
              <div>
                <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-black mb-4">
                  What are the symptoms of mental health problems?
                </h2>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4">
                  Symptoms include persistent sadness, anxiety, mood 
                  swings, social withdrawal, and changes in sleep or 
                  appetite. Health City identifies these symptoms through 
                  assessments and offers <span className="underline">support</span> to manage and treat them 
                  effectively.
                </p>
              </div>

              {/* How to take care */}
              <div>
                <h3 className="text-base md:text-lg font-bold text-black mb-3">
                  How to take care of mental health?
                </h3>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4">
                  <span className="underline">Mental health care</span> involves a balanced lifestyle, stress 
                  management, and seeking support when needed. Health 
                  City offers counselling, therapy, and coping strategies to 
                  help you maintain mental well-being and develop 
                  personalized care plans.
                </p>
              </div>

              {/* Private care cost */}
              <div>
                <h3 className="text-base md:text-lg font-bold text-black mb-3">
                  How much is private mental health care in the UK?
                </h3>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                  Private mental health care in the UK typically costs 
                  between £40 and £150 per hour, depending on the service. 
                  Health City offers competitively priced private care, 
                  providing expert support without long waiting times.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-black text-center mb-8 md:mb-12">
            Frequently asked questions
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-200">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex justify-between items-center py-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm md:text-base font-medium text-black pr-4">
                    {faq.question}
                  </span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <div className="pb-4">
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                      {/* Placeholder content - this would be replaced with actual FAQ answers */}
                      This is where the detailed answer for "{faq.question}" would appear. 
                      Health City provides comprehensive information about all mental health care topics.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}